import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColorOrString, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import React, { useEffect, useRef, useState } from 'react'

import Badge from '../../atoms/badge'
import Typography, { TextType } from '../../atoms/typography'

interface Tab {
  label: string
  component?: React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
}

export interface TabsProps {
  tabs: Array<Tab>
  activeTab: number
  customTabComponent?: React.ReactNode // This goes at the end of the tabs, it's more likely used for action buttons
  wrapperClassName?: string
  tabClassName?: string
  tabTextVariant?: TextType
  activeTabColor?: TailwindColorOrString
  onTabChange: (tabIndex: number, label: string) => void
}

const LineTab: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  customTabComponent,
  activeTabColor = tailwindColors.primary.DEFAULT,
  tabTextVariant = 'label-medium',
  wrapperClassName,
  tabClassName,
  onTabChange
}) => {
  const [borderStyle, setBorderStyle] = useState({ left: 0, width: 0 })
  const tabsRef = useRef<HTMLButtonElement[]>([])

  const updateBorderStyle = () => {
    const activeTabElement = tabsRef.current[activeTab]
    if (activeTabElement) {
      const { offsetLeft, offsetWidth } = activeTabElement
      setBorderStyle({ left: offsetLeft, width: offsetWidth })
    }
  }

  useEffect(() => {
    updateBorderStyle()
    // Add resize listener
    window.addEventListener('resize', updateBorderStyle)

    return () => {
      window.removeEventListener('resize', updateBorderStyle)
    }
  }, [activeTab, tabs])

  return (
    <div className={cn('flex flex-col relative h-full', wrapperClassName)}>
      <div className="flex relative border-b border-b-white-active">
        {tabs.map((tab, index) => (
          <button
            type={'button'}
            key={index}
            ref={(el) => (tabsRef.current[index] = el!)}
            className={cn(
              'cursor-pointer py-2.5 px-3 relative whitespace-nowrap text-center',
              (index !== tabs.length - 1 || customTabComponent) && 'mr-2.5',
              tabClassName,
              { 'cursor-not-allowed ': tab.disabled }
            )}
            onClick={() => {
              if (!tab.disabled) onTabChange(index, tab.label)
            }}
          >
            <div className="flex gap-2 items-center justify-center">
              {tab.icon && (
                <Badge
                  icon={tab.icon}
                  variant={index === activeTab ? 'primary' : 'grey'}
                  size="small"
                  className="p-1"
                />
              )}
              <Typography
                className="transition-colors"
                variant={tabTextVariant}
                color={index === activeTab ? activeTabColor : tailwindColors.grey.light.active}
              >
                {tab.label}
              </Typography>
            </div>
          </button>
        ))}
        {customTabComponent}
        <div
          className={'absolute -bottom-0.5 h-0.5 transition-all duration-300'}
          style={{
            left: borderStyle.left,
            width: borderStyle.width,
            backgroundColor: activeTabColor
          }}
        />
      </div>
      <div>{tabs[activeTab].component}</div>
    </div>
  )
}

export default LineTab
